import { useParams, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { getVisit, getClinicVisitType, getVisitEntriesTimeline, updateVisitEntry } from "../../../apis/visit";
import { Visit } from "../../../interfaces/Visit";
import { format } from "date-fns";
import { useAppDispatch } from "../../../hooks";
import { changeVisit } from "../../../features/visit/visitSlice";
import { getPatient } from "../../../apis/patient";
import { useNotification } from "../../../NotificationContext";
import LoaderComponent from "../../../components/LoaderComponent";
import Modal from "../../../components/Modal";
import IPDEntryCard from "./IPDEntryCard";
import IPDEntryForm from "./IPDEntryForm";
import IPDAdmissionForm from "./forms/IPDAdmissionForm";
import PaymentModal from "../../../components/PaymentModal"; 
import AddLabOrder from "../../laboratory/AddLabOrder"; 
import { getLayout, getLayoutById } from "../../../apis/visit";
import ServiceModal from "./forms/ServicesForm";
import IPDBillingModal from "./forms/IPDBillingModal";
import GeneralCommentForm from "./forms/GeneralCommentForm";

import { Collapse } from "antd";
import ViewLabOrder from "../../laboratory/ViewLabOrder";
import { Modal as AntModal } from "antd";

const IPDTimeline = () => {

  const [visit, setVisit] = useState<Visit>();
  const [patient, setPatient] = useState<any>(null);
  const [clinicVisitType, setClinicVisitType] = useState<any>(null);
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isLoader, setLoader] = useState<boolean>(false);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showAdmissionForm, setShowAdmissionForm] = useState(false);
  const [entryType, setEntryType] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [, setClinicVisitTypeLayout] = useState<any>(null);
  const [, setVisitTypeLayout] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLabOrderModal, setShowLabOrderModal] = useState(false);
  const [selectedLabOrderId, setSelectedLabOrderId] = useState<number | undefined>(undefined);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [isLabModalOpen, setIsLabModalOpen] = useState(false);
  
  const [, setIpdLayouts] = useState<{
    assessment: any;
    doctor_rounds: any;
    nursing: any;
  } | null>(null);
  const [, setIsLoading] = useState<boolean>(false);
  const [timelineEntries, setTimelineEntries] = useState<any[]>([]);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState<boolean>(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsModalEntry, setCommentsModalEntry] = useState<any>(null);
  const [openLabReport, setOpenLabReport] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<number | undefined>(undefined);
  // Effects are declared after callbacks to avoid use-before-define lint errors.

  const fetchPatient = (patientId: any) => {
    getPatient(patientId).then((res: any) => {
      setPatient(res.data.data);
    });
  };

  const fetchClinicVisitType = (id: any) => {
    getClinicVisitType(id).then((res: any) => {
      setClinicVisitType(res.data);
    });
  };

  // Process IPD layouts from API response (converts snake_case to camelCase)
  const processIPDLayouts = useCallback((layoutData: any, flowTypeId: number | string) => {
    const processed: any = {
      assessment: null,
      doctor_rounds: null,
      nursing: null,
    };

    console.log("layoutData", layoutData);

    // Handle both camelCase (doctorRounds) and snake_case (doctor_rounds) formats
    const entryTypeMap: Record<string, string> = {
      "assessment": "assessment",
      "doctorRounds": "doctor_rounds",
      "doctor_rounds": "doctor_rounds",
      "nursing": "nursing",
    };

    Object.keys(entryTypeMap).forEach((key) => {
      const entryType = entryTypeMap[key];
      const entry = layoutData[key] || layoutData[entryType];

      if (entry) {
        // Convert grid_items (snake_case from API) to gridItems (camelCase for frontend)
        const gridItems = entry.grid_items || entry.gridItems || [];
        const layout = entry.layout || [];

        // Ensure layout items have string keys (i should be string)
        const processedLayout = layout.map((item: any) => ({
          ...item,
          i: String(item.i || item.id || ""),
        }));

        processed[entryType] = {
          gridItems: gridItems,
          layout: processedLayout,
        };
      }
    });

    setIpdLayouts(processed);

    // Save to localStorage for IPDEntryForm to use
    // Use clinicVisitTypeId as flowTypeId (since IPD visit types are the flow types)
    const flowId =
      flowTypeId || visit?.clinicVisitTypeId || visit?.meta?.ipdFlowTypeId || 1;
    const layoutsToSave: any = {};
    Object.keys(processed).forEach((key) => {
      if (
        processed[key] &&
        processed[key].gridItems &&
        processed[key].gridItems.length > 0
      ) {
        layoutsToSave[key] = processed[key];
      }
    });

    if (Object.keys(layoutsToSave).length > 0) {
      localStorage.setItem(
        `ipd_layouts_${flowId}`,
        JSON.stringify(layoutsToSave)
      );
      console.log(`Saved IPD layouts for flow type ${flowId}:`, layoutsToSave);
    }
  }, [visit?.clinicVisitTypeId, visit?.meta?.ipdFlowTypeId]);

  const getVisitTypeLayout = useCallback((id: any, data: any) => {
    setIsLoading(true);
    getLayout(id).then((res: any) => {
      setClinicVisitTypeLayout(res.data);
      // Process IPD layouts if they exist
      // IPD format: { layout: { layout: { assessment: {...}, doctorRounds: {...}, nursing: {...} } } }
      const layout = res.data?.layout;
      if (layout && typeof layout === 'object' && !Array.isArray(layout)) {
        // Check if it has nested layout structure (IPD format)
        if (layout.layout && typeof layout.layout === 'object' && !Array.isArray(layout.layout)) {
          // Check if it has IPD entry types (assessment, doctorRounds/doctor_rounds, nursing)
          const ipdLayoutData = layout.layout;
          if ('assessment' in ipdLayoutData || 'doctorRounds' in ipdLayoutData || 'doctor_rounds' in ipdLayoutData || 'nursing' in ipdLayoutData) {
            processIPDLayouts(ipdLayoutData, id);
          }
        } else if ('assessment' in layout || 'doctorRounds' in layout || 'doctor_rounds' in layout || 'nursing' in layout) {
          // Fallback: check if layout itself has IPD entry types (for backward compatibility)
          processIPDLayouts(layout, id);
        }
      }
      setIsLoading(false);
    }).catch((error) => {
      console.error("Failed to load layout:", error);
      setIsLoading(false);
    });
  }, [processIPDLayouts]);
  
  const getVisitTypeLayoutById = useCallback((id: any, data: any) => {
    setIsLoading(true);
    getLayoutById(id).then((res: any) => {
      setVisitTypeLayout(res.data);
      // Process IPD layouts if they exist
      // IPD format: { layout: { layout: { assessment: {...}, doctorRounds: {...}, nursing: {...} } } }
      const layout = res.data?.layout;
      if (layout && typeof layout === 'object' && !Array.isArray(layout)) {
        // Get clinic_visit_type_id from the layout record to use as flowTypeId
        const clinicVisitTypeId = res.data?.clinic_visit_type_id || visit?.clinicVisitTypeId;
        
        // Check if it has nested layout structure (IPD format)
        if (layout.layout && typeof layout.layout === 'object' && !Array.isArray(layout.layout)) {
          // Check if it has IPD entry types (assessment, doctorRounds/doctor_rounds, nursing)
          const ipdLayoutData = layout.layout;
          if ('assessment' in ipdLayoutData || 'doctorRounds' in ipdLayoutData || 'doctor_rounds' in ipdLayoutData || 'nursing' in ipdLayoutData) {
            processIPDLayouts(ipdLayoutData, clinicVisitTypeId);
          }
        } else if ('assessment' in layout || 'doctorRounds' in layout || 'doctor_rounds' in layout || 'nursing' in layout) {
          // Fallback: check if layout itself has IPD entry types (for backward compatibility)
          processIPDLayouts(layout, clinicVisitTypeId);
        }
      }
      setIsLoading(false);
    }).catch((error) => {
      console.error("Failed to load layout by ID:", error);
      setIsLoading(false);
    });
  }, [processIPDLayouts, visit?.clinicVisitTypeId]);

  const fetchVisit = useCallback(async (id: any) => {
    setLoader(true);
    await getVisit(id).then(async (res: any) => {
      dispatch(changeVisit(res));
      setVisit(res);
      setLoader(false);
      // setPatient(res.patient);
      const patientId = params.patientId || res.patient?.id;
      if (patientId) {
        fetchPatient(patientId);
      } else {
        setPatient(res.patient);
      }
      // Fetch clinic visit type if available from visit but not in params
      if (res.clinicVisitTypeId && !params.clinicVisitTypeId) {
        fetchClinicVisitType(res.clinicVisitTypeId);
      }
      // After visit is loaded, fetch layout if clinicVisitTypeId is available
      if (res.clinicVisitTypeId && !res.clinicVisitTypeLayoutId) {
        getVisitTypeLayout(res.clinicVisitTypeId, res.meta);
      }
      // Fetch timeline entries
      if (res.id) {
        fetchTimelineEntries(res.id);
      }
    });
  }, [dispatch, getVisitTypeLayout, params.clinicVisitTypeId]);

  useEffect(() => {
    if (params.visitId) {
      fetchVisit(params.visitId);
    }
    // if (params.patientId) {
    //   fetchPatient(params.patientId);
    // }
    if (params.clinicVisitTypeId) {
      fetchClinicVisitType(params.clinicVisitTypeId);
      getVisitTypeLayout(parseInt(params.clinicVisitTypeId), {});
    }
  }, [fetchVisit, getVisitTypeLayout, params.visitId, params.clinicVisitTypeId, params.patientId]);

  // Auto-show admission form if visit doesn't exist
  useEffect(() => {
    // Only show admission form if:
    // 1. No visitId in params (new admission)
    // 2. Visit is not loaded
    // 3. We have patientId and clinicVisitTypeId
    if (!params.visitId && !visit && params.patientId && params.clinicVisitTypeId) {
      setShowAdmissionForm(true);
    }
  }, [params.visitId, params.patientId, params.clinicVisitTypeId, visit]);

  useEffect(() => {
    if (visit) {
      if (visit.clinicVisitTypeLayoutId) {
        getVisitTypeLayoutById(visit.clinicVisitTypeLayoutId, visit.meta);
      } else if (visit.clinicVisitTypeId) {
        // If no layout ID, try to get layout by clinic visit type ID
        getVisitTypeLayout(visit.clinicVisitTypeId, visit.meta);
      }
    }
  }, [getVisitTypeLayout, getVisitTypeLayoutById, visit]);

  const fetchTimelineEntries = async (visitId: number) => {
    setIsLoadingTimeline(true);
    try {
      const response: any = await getVisitEntriesTimeline(visitId);
      // The API returns { data: [...] } structure
      const entries = response?.data || response?.entries || [];
      setTimelineEntries(entries);
    } catch (error) {
      console.error("Failed to fetch timeline entries:", error);
      setTimelineEntries([]);
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  const handleCommentsClick = (entry: any) => {
    setCommentsModalEntry(entry);
    setShowCommentsModal(true);
  };

  const handleCommentsModalClose = () => {
    setShowCommentsModal(false);
    setCommentsModalEntry(null);
  };

  const handleCommentsSave = async (data: { comments: string }) => {
    if (!visit?.id || !commentsModalEntry?.id) return;
    try {
      const formData = {
        ...(commentsModalEntry.data || {}),
        comments: data.comments,
      };
      await updateVisitEntry(visit.id, Number(commentsModalEntry.id), {
        form_data: formData,
      });
      showNotification("success", "Comments saved", "", "topRight");
      handleCommentsModalClose();
      fetchTimelineEntries(visit.id);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Failed to save comments";
      showNotification("error", msg, "", "topRight");
    }
  };

  const handleAddEntry = (type: string) => {
    // If visit doesn't exist and user clicks assessment, show admission form
    if (!visit && type === "assessment") {
      setShowAdmissionForm(true);
      return;
    }
    
    // If visit doesn't exist for other entry types, show message
    if (!visit) {
      showNotification(
        "warning",
        "Please complete admission first",
        "You need to create an admission before adding entries",
        "topRight"
      );
      return;
    }
    
    if (type === "payment") {
      setShowPaymentModal(true);
      return;
    }

    if (type === "billing") {
      setShowBillingModal(true);
      return;
    }

    if (type === "lab_request") {
      setShowLabOrderModal(true);
      return;
    }

    if (type === "services") {
      setShowServiceModal(true);
      return;
    }

    setEntryType(type);
    setEditingEntry(null);
    setShowEntryForm(true);
    
    console.log("State updated - entryType:", type, "showEntryForm: true");
  };

  const handleEditEntry = (entry: any) => {
    // If entry type is admission, open admission form with pre-filled values
    const normalizedType = String(entry?.type || "").toLowerCase();
    if (normalizedType.includes("admission")) {
      setEditingEntry(entry);
      setShowAdmissionForm(true);
      return;
    }

    setEditingEntry(entry);
    setEntryType(entry.type);
    setShowEntryForm(true);
  };

  const handleEntrySaved = () => {
    setShowEntryForm(false);
    setEntryType(null);
    setEditingEntry(null);
    if (params.visitId) {
      fetchVisit(params.visitId);
      // Also refresh timeline
      fetchTimelineEntries(parseInt(params.visitId));
    }
  };

  const handleAdmissionSaved = (savedVisit: any) => {
    setShowAdmissionForm(false);
    setEditingEntry(null);
    dispatch(changeVisit(savedVisit));
    setVisit(savedVisit);
    // Refresh timeline entries
    if (savedVisit.id) {
      fetchTimelineEntries(savedVisit.id);
      // Only navigate if it's a new visit
      if (!params.visitId) {
        navigate(`/ipd/visits/${savedVisit.id}`);
      }
    }
  };

  const handleCancelAdmission = () => {
    setShowAdmissionForm(false);
    setEditingEntry(null);
  };

  const handleCancelEntry = () => {
    setShowEntryForm(false);
    setEntryType(null);
    setEditingEntry(null);
  };

  // Get IPD entries from timeline API, sorted by created_at (latest first)
  const getIPDEntries = () => {
    if (!timelineEntries || timelineEntries.length === 0) return [];
    // Timeline entries should already be sorted in descending order by backend
    // But we'll ensure they're sorted by created_at descending (latest first)
    return [...timelineEntries].sort(
      (a: any, b: any) =>
        new Date(b.created_at || b.createdAt || 0).getTime() - 
        new Date(a.created_at || a.createdAt || 0).getTime()
    );
  };

  const ipdEntries = getIPDEntries();
  const resolvedPatientId = params.patientId
    ? parseInt(params.patientId)
    : visit?.patientId;
  const resolvedClinicVisitTypeId = params.clinicVisitTypeId
    ? parseInt(params.clinicVisitTypeId)
    : visit?.clinicVisitTypeId;

  return (
    <div className="flex flex-col flex-grow">
      <main className="flex flex-col flex-grow bg-[#ECEFFF] rounded-md">
        <div className="p-2 space-y-4 sticky top-0 z-10 bg-[#ECEFFF]/80 backdrop-blur border-b border-gray-200 shadow-sm">
          <div className="bg-white rounded">
            <Collapse
              items={[
                {
                  key: "1",
                  label: (
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {patient?.firstName} {patient?.lastName}({patient?.registrationNumber}) - {visit?.clinicVisitType?.visitType.name
                            ? visit.clinicVisitType.visitType.name
                            : clinicVisitType?.name}
                    </span>
                  ),
                  children: (
                    <div className="grid grid-cols-3 gap-4 p-4">
                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">
                          Patient Name:{" "}
                        </span>
                        <span className="ml-2 text-gray-800 capitalize">
                          {patient?.firstName} {patient?.lastName} ({patient?.registrationNumber})
                        </span>
                      </div>

                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">Phone: </span>
                        <span className="ml-2 text-gray-800">{patient?.phone}</span>
                      </div>

                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">Patient Habits: </span>
                        <span className="ml-2 text-gray-800">
                          {(() => {
                            const habits = [];
                            if (patient?.isAlcoholic) habits.push("Alcohol");
                            if (patient?.isSmoking) habits.push("Smoking");
                            if (patient?.isBloodDisorder) habits.push("Blood Disorder");
                            if (patient?.isBloodPressure) habits.push("Blood Pressure");
                            if (patient?.isDiabetic) habits.push("Diabetic");
                            return habits.length > 0 ? habits.join(", ") : "No habits recorded";
                          })()}
                        </span>
                      </div>

                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">Previous Surgeries: </span>
                        <span className="ml-2 text-gray-800">
                          {patient?.historyData?.previousSurgeries?.map((s: any) => s.label).join(", ") || "-"}
                        </span>
                      </div>

                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">Allergies: </span>
                        <span className="ml-2 text-gray-800">
                          {patient?.historyData?.allergies?.map((a: any) => a.label).join(", ") || "-"}
                        </span>
                      </div>

                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">Previous Diseases: </span>
                        <span className="ml-2 text-gray-800">
                          {patient?.historyData?.previousDiseases?.map((d: any) => d.label).join(", ") || "-"}
                        </span>
                      </div>

                      <div className="flex w-full">
                        <span className="font-medium text-gray-900">Ongoing Medicines: </span>
                        <span className="ml-2 text-gray-800">
                          {patient?.historyData?.ongoingmedicines?.map((d: any) => d.label).join(", ") || "-"}
                        </span>
                      </div>

                      <div className="flex flex-wrap text-sm">
                        <span className="font-medium">Lab Tests: </span>
                        {patient?.labOrders?.length > 0 ? (
                          <span className="ml-2 text-gray-800">
                            {patient.labOrders.map((order: any, index: number) => (
                              <span key={order.id}>
                                <span
                                  className="text-primary underline cursor-pointer"
                                  onClick={() => {
                                    setSelectedLabOrderId(order.id);
                                    setIsLabModalOpen(true);
                                  }}
                                >
                                  {order.labTest?.name}
                                </span>
                                {index < patient.labOrders.length - 1 && ", "}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="ml-2 text-gray-500">No lab tests</span>
                        )}
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>

          <div className="p-4 bg-white rounded shadow-sm">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleAddEntry("assessment")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Assessment
              </button>
              <button
                onClick={() => handleAddEntry("doctor_rounds")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Doctor Rounds
              </button>
               <button
                onClick={() => handleAddEntry("general_comment")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Comments
              </button>
              <button
                onClick={() => handleAddEntry("nursing")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Nursing
              </button>
              <button
                onClick={() => handleAddEntry("pharmacy_request")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Pharmacy
              </button>
              <button
                onClick={() => handleAddEntry("lab_request")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Lab Reports
              </button>
              <button
                onClick={() => handleAddEntry("room_transfer")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Room 
              </button>
             
              <button
                onClick={() => handleAddEntry("services")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Services
              </button>
              <button
                onClick={() => handleAddEntry("ot")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + OT
              </button>
              <button
                onClick={() => handleAddEntry("billing")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Billing
              </button>
              <button
                onClick={() => handleAddEntry("discharge")}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                + Discharge
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <ol className="relative border-l-2 border-primary">
            {(isLoader || isLoadingTimeline) ? (
              <li className="ml-4">
                <LoaderComponent />
              </li>
            ) : (
              ipdEntries.map((entry: any, index: number) => {
                // Transform API entry format (supports both snake_case and camelCase)
                const transformedEntry = {
                  id: entry.id,
                  title: entry.title,
                  type: entry.entryType || entry.entry_type,
                  date: entry.createdAt || entry.created_at,
                  data: entry.formData || entry.form_data || {},
                  userId: entry.createdBy?.id || entry.created_by?.id,
                  clinicVisitTypeLayoutId:
                    entry.clinicVisitTypeLayoutId ||
                    entry.clinic_visit_type_layout_id,
                };
                return (
                  <li className="ml-4" key={entry.id || index}>
                    <IPDEntryCard
                      entry={transformedEntry}
                      visit={visit}
                      onEdit={() => handleEditEntry(transformedEntry)}
                      onRefresh={() => {
                        if (params.visitId) {
                          fetchVisit(params.visitId);
                          fetchTimelineEntries(parseInt(params.visitId));
                        }
                      }}
                      onCommentsClick={handleCommentsClick}
                    />
                  </li>
                );
              })
            )}
          </ol>
        </div>
      </main>

      {/* Admission Form Modal */}
      {showAdmissionForm && resolvedPatientId && resolvedClinicVisitTypeId && (
        <Modal
          width="max-w-2xl"
          title={editingEntry ? "Edit IPD Admission" : "IPD Admission"}
          onClose={handleCancelAdmission}
          modalContent={
            <IPDAdmissionForm
              patientId={resolvedPatientId}
              clinicVisitTypeId={resolvedClinicVisitTypeId}
              initialData={(editingEntry?.data || visit) ? {
                admittingDoctorId:
                  editingEntry?.data?.admittingDoctorId ||
                  editingEntry?.data?.userId ||
                  visit?.userId,
                chiefComplaint:
                  editingEntry?.data?.chiefComplaint ||
                  editingEntry?.data?.chief_complaint ||
                  (visit as any)?.chiefComplaint ||
                  (visit as any)?.chief_complaint,
                provisionalDiagnosis:
                  editingEntry?.data?.provisionalDiagnosis ||
                  editingEntry?.data?.provisional_diagnosis ||
                  visit?.provisionalDiagnosis ||
                  [],
                roomId:
                  editingEntry?.data?.roomId ||
                  editingEntry?.data?.room_id ||
                  visit?.meta?.roomId,
                paymentType:
                  editingEntry?.data?.paymentType ||
                  editingEntry?.data?.payment_type ||
                  visit?.meta?.paymentType ||
                  "self",
                admissionDate:
                  editingEntry?.data?.admissionDate ||
                  editingEntry?.data?.admission_date ||
                  visit?.meta?.admissionDate ||
                  format(new Date(visit?.date || new Date()), "yyyy-MM-dd"),
                admissionTime:
                  editingEntry?.data?.admissionTime ||
                  editingEntry?.data?.admission_time ||
                  visit?.meta?.admissionTime ||
                  format(new Date(visit?.date || new Date()), "HH:mm"),
              } : undefined}
              visitId={visit?.id}
              onSave={handleAdmissionSaved}
              onCancel={handleCancelAdmission}
            />
          }
        />
      )}

      {/* Entry Form Modal */}
      {showEntryForm && entryType && visit && (
        <IPDEntryForm
          visit={visit}
          entryType={entryType}
          entry={editingEntry}
          onSave={handleEntrySaved}
          onCancel={handleCancelEntry}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          total={0}                
          discount={0}
          patientName={`${patient?.firstName || ""} ${patient?.lastName || ""}`}
          patientId={patient?.id}
          sourceType="pathology"       
          sourceId={selectedLabOrderId}
          onPaymentSuccess={() => {
            setShowPaymentModal(false);
            if (visit?.id) {
              fetchVisit(visit.id);
              fetchTimelineEntries(visit.id);
            }
          }}
        />
      )}

      {showLabOrderModal && (
        <Modal
          modalContent={
            <AddLabOrder
              orderId={null}
              visitId={visit?.id}
              patientId={patient?.id} 
              hideBillingFields={true}
              isFromIPD={true} 
              closeModal={() => setShowLabOrderModal(false)}
              onOrderCreated={(order) => {
                showNotification(
                  "success",
                  `Lab order ${order.orderNumber} created successfully`,
                  "",
                  "topRight"
                );
                setSelectedLabOrderId(order.id);
                setShowLabOrderModal(false);
                if (params.visitId) {
                  fetchVisit(params.visitId);
                  fetchTimelineEntries(parseInt(params.visitId));
                }
              }}
            />
          }
        />
      )}

      {showServiceModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black/50">
          <ServiceModal
            isOpen={showServiceModal}
            onClose={() => setShowServiceModal(false)}
            visitId={visit?.id}
            onServiceAdded={async (savedServices) => {
              if (!visit?.id) {
                showNotification(
                  "error",
                  "Visit not found",
                  "Please ensure visit is loaded before adding services",
                  "topRight"
                );
                setShowServiceModal(false);
                return;
              }

              try {
                // Services are already saved by ServiceModal via updateVisitServices API
                // Backend implements delete-and-add: deletes all existing services and adds only the ones in payload
                // So we just need to update the local visit state with the saved services
                const updatedVisit = {
                  ...visit,
                  services: savedServices,
                };
                
                // Update the visit in state
                dispatch(changeVisit(updatedVisit));
                setVisit(updatedVisit);

                showNotification(
                  "success",
                  "Services updated successfully",
                  "",
                  "topRight"
                );
                setShowServiceModal(false);

                // Refresh visit and timeline to get latest data from backend
                fetchVisit(visit.id);
                fetchTimelineEntries(visit.id);
              } catch (error: any) {
                console.error("Failed to update visit state with services:", error);
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.data?.message ||
                  "Failed to update services";
                showNotification("error", errorMessage, "", "topRight");
              }
            }}
          />
        </div>
      )}

      {showBillingModal && visit?.id && patient?.id && (
        <IPDBillingModal
          isOpen={showBillingModal}
          onClose={() => setShowBillingModal(false)}
          visitId={visit.id}
          patientId={patient.id}
        />
      )}
<AntModal
  open={isLabModalOpen}
  footer={null}
  onCancel={() => setIsLabModalOpen(false)}
  width={1100}
  title="Lab Test Reports"
>
  {patient?.id && (
    <ViewLabOrder
      isModal={true}
      orderId={selectedLabOrderId}
      onClose={() => setIsLabModalOpen(false)}
    />
  )}
</AntModal>

      {openLabReport && (
        <AntModal
          open={openLabReport}
          onCancel={() => setOpenLabReport(false)}
          footer={null}
          width={1100} 
          title="Lab Test Reports"
          destroyOnClose
        >
          <ViewLabOrder
            isModal={true}
            patientId={selectedPatientId ?? undefined}
            onClose={() => setOpenLabReport(false)}
          />
        </AntModal>
      )}
      {/* Comments modal (for doctor rounds) - uses GeneralCommentForm */}
      {showCommentsModal && commentsModalEntry && (
        <Modal
          width="max-w-2xl"
          title="Comments"
          onClose={handleCommentsModalClose}
          modalContent={
            <GeneralCommentForm
              initialData={commentsModalEntry?.data}
              onSave={handleCommentsSave}
              onCancel={handleCommentsModalClose}
            />
          }
        />
      )}

    </div>
  );
};

export default IPDTimeline;


